import PropTypes from "prop-types";
import React from "react";

import useOutputReportStore from "../../../../store/OutputReport.store";
import { getOutputType } from "../../../../utils/helpers/dataTable";
import BarChart from "../../../Chart/Bar";

import {
  createChartLegend,
  createTransitionScenario,
  getChartDataset,
  getChartOptions,
  getColorSequence,
  getDefaultBarChartLabels,
  getXlabels,
  getYlabels,
} from "../../../../utils/helpers/chart";

const MaturityChart = ({ id, isFullWidth }) => {
  const reportName = "Maturity";
  const data = useOutputReportStore((state) => state.data);
  const { reportType, assetType } = getOutputType(data);

  const maturityConfig = {
    transition: {
      hasTransitionScenario: true,
    },
    cash: {
      hasTransitionScenario: false,
    },
  };

  const excludedXlabels = {
    transition: {
      corp: [],
      muni: [],
      treasury: [],
    },
    cash: {
      corp: [],
      muni: [],
      treasury: [],
      tbill: ["Sample Representative Portfolio"],
    },
  };

  const reportData = data[reportName];

  const yLabels = getYlabels(reportData, ["Total"]);
  const { xLabels, xLabelOverrides } = getXlabels(
    reportData,
    excludedXlabels[reportType][assetType],
    assetType,
    reportType,
    reportName
  );

  const barChartId = id;

  // Chart Options
  const config = {
    isFullWidth,
    isLegend: true,
    tooltipValueType: "percent-x-hundred-fixed",
    yTicksType: "percent-x-hundred",
    yStepSize: 0.5,
    isStacked: true,
    hasYannotation: false,
    ...maturityConfig[reportType],
  };
  const chartOptions = getChartOptions(config);

  // Chart Styles
  const chartStyles = getColorSequence();

  // Chart Dataset
  const chartDatasets = getChartDataset(reportData, yLabels, xLabels);

  const transitionFormattedLabels = getDefaultBarChartLabels();
  const cashFormattedLabel = [
    ["Target", "Portfolio*"],
    ["Sample Representative Portfolio"],
    ["Reference", "Index"],
  ];
  // Chart Data Labels
  const chartLabels = {
    transition: {
      muni: transitionFormattedLabels,
      corp: transitionFormattedLabels,
      treasury: transitionFormattedLabels,
    },
    cash: {
      muni: isFullWidth ? xLabelOverrides : cashFormattedLabel,
      corp: isFullWidth ? xLabelOverrides : cashFormattedLabel,
      treasury: isFullWidth ? xLabelOverrides : cashFormattedLabel,
      tbill: isFullWidth
        ? xLabelOverrides
        : [
            ["Target", "Portfolio*"],
            ["Reference", "Index**"],
          ],
    },
  };

  // Formatted Chart Data
  const chartData = {
    labels: chartLabels[reportType][assetType],
    datasets: chartDatasets
      .map((item, idx) => {
        return {
          ...item,
          ...chartStyles[idx],
        };
      })
      .reverse(),
  };

  // Label for Transition Scenarios
  const transitionScenarioId = "maturityTransitionScenario";
  const transitionScenario = createTransitionScenario(
    transitionScenarioId,
    barChartId
  );

  // Legend for Maturity
  const chartLegend = createChartLegend({
    containerId: barChartId,
    legendId: `legend-${barChartId}`,
    legendTitle: "Maturity (by bucket)",
    isReversed: true,
    isFullWidth,
  });

  return (
    <BarChart
      id={barChartId}
      data={chartData}
      options={chartOptions}
      plugins={[
        config.isLegend ? chartLegend : {},
        config.hasTransitionScenario ? transitionScenario : {},
      ]}
      ariaLabel="Maturity Report Bar Chart"
      height={isFullWidth ? "450px" : "390px"}
    />
  );
};

MaturityChart.propTypes = {
  id: PropTypes.string,
  isFullWidth: PropTypes.bool,
};

MaturityChart.defaultProps = {
  id: "bar-chart-maturity",
  isFullWidth: true,
};

export default MaturityChart;
